import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Box, Button, CircularProgress, Container, IconButton, Tooltip, Typography } from '@mui/material';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DoneIcon from '@mui/icons-material/Done';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import styled from '@emotion/styled';
import PageHeader from '../components/PageHeader';
import { AIMarkdown } from '../components/AIResponseDialog';
import { getNotifications, markNotificationRead, markAllNotificationsRead, deleteNotification } from '../redux/ducks/notification';
import { formatDateTime } from '../utils/dateUtils';
import { resolveNotificationLink, notificationLinkLabel, previewOf } from '../utils/navigateUtils';

const BODY_PREVIEW_MAX = 600;

const StyledContainer = styled(Container)(({ theme }) => ({
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(6),
}));

const Rows = styled(Box)(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    overflow: 'hidden',
}));

const Row = styled(Box, { shouldForwardProp: (prop) => prop !== '$unread' })(({ theme, $unread }) => ({
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr) auto',
    gap: theme.spacing(1.5),
    alignItems: 'flex-start',
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: $unread ? theme.palette.action.hover : 'transparent',
    '&:last-of-type': {
        borderBottom: 'none',
    },
}));

const StatusMark = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: theme.spacing(2),
    paddingTop: theme.spacing(0.75),
}));

const UnreadDot = styled(FiberManualRecordIcon)(({ theme }) => ({
    fontSize: theme.typography.size.xxs,
    color: theme.palette.primary.main,
}));

const Subject = styled(Typography, { shouldForwardProp: (prop) => prop !== '$unread' })(({ theme, $unread }) => ({
    fontWeight: $unread ? theme.typography.fontWeightBold : theme.typography.fontWeightRegular,
    color: theme.palette.text.primary,
    overflowWrap: 'anywhere',
}));

const SubjectLink = styled(Subject)({
    textDecoration: 'none',
    '&:hover': {
        textDecoration: 'underline',
    },
});

const BodyText = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
    whiteSpace: 'pre-line',
    overflowWrap: 'anywhere',
}));

const ReminderBody = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.secondary,
}));

const Meta = styled(Typography)(({ theme }) => ({
    display: 'block',
    marginTop: theme.spacing(0.75),
    color: theme.palette.text.disabled,
}));

const RowActions = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
}));

const CenteredState = styled(Box)(({ theme }) => ({
    padding: theme.spacing(6),
    textAlign: 'center',
    color: theme.palette.text.secondary,
}));

const LoadMoreRow = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(2),
    textAlign: 'center',
}));

const isReminder = (notification) => notification.objectType === 'Reminder';

const MyNotifications = () => {
    const dispatch = useDispatch();
    const { items, unreadCount, pagination } = useSelector((state) => state.notifications);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        dispatch(getNotifications({ limit: 25 }, () => setLoading(false)));
    }, [dispatch]);

    useEffect(() => {
        load();
    }, [load]);

    const handleMarkAll = () => {
        dispatch(markAllNotificationsRead());
    };

    const handleMarkRead = (id) => {
        dispatch(markNotificationRead(id));
    };

    const handleDelete = (id) => {
        dispatch(deleteNotification(id));
    };

    const handleLoadMore = () => {
        if (pagination.page >= pagination.pages) return;
        setLoadingMore(true);
        dispatch(getNotifications({ page: pagination.page + 1, limit: pagination.limit }, () => setLoadingMore(false)));
    };

    const subtitle = loading ? 'Loading…' : unreadCount > 0 ? `${unreadCount > 99 ? '99+' : unreadCount} unread` : 'Nothing unread';

    return (
        <StyledContainer maxWidth="md">
            <PageHeader
                title="Notifications"
                subtitle={subtitle}
                actions={
                    unreadCount > 0 && (
                        <Button size="small" startIcon={<DoneAllIcon />} onClick={handleMarkAll}>
                            Mark all as read
                        </Button>
                    )
                }
            />
            {loading ? (
                <CenteredState>
                    <CircularProgress size={28} />
                </CenteredState>
            ) : items.length === 0 ? (
                <CenteredState>
                    <Typography variant="body1">No notifications yet</Typography>
                </CenteredState>
            ) : (
                <>
                    <Rows>
                        {items.map((notification) => {
                            const href = resolveNotificationLink(notification);
                            const meta = [notification.triggeringUser?.fullName, notification.account?.name, formatDateTime(notification.createdAt)]
                                .filter(Boolean)
                                .join(' · ');
                            return (
                                <Row key={notification._id} $unread={!notification.read}>
                                    <StatusMark>{notification.read ? null : <UnreadDot />}</StatusMark>
                                    <Box>
                                        {href ? (
                                            <SubjectLink
                                                variant="body2"
                                                component={Link}
                                                to={href}
                                                $unread={!notification.read}
                                                onClick={() => !notification.read && handleMarkRead(notification._id)}
                                            >
                                                {notification.subject}
                                            </SubjectLink>
                                        ) : (
                                            <Subject variant="body2" $unread={!notification.read}>
                                                {notification.subject}
                                            </Subject>
                                        )}
                                        {isReminder(notification) ? (
                                            <ReminderBody>
                                                <AIMarkdown>{notification.body}</AIMarkdown>
                                            </ReminderBody>
                                        ) : (
                                            <BodyText variant="body2">{previewOf(notification.body, BODY_PREVIEW_MAX)}</BodyText>
                                        )}
                                        <Meta variant="caption">{meta}</Meta>
                                    </Box>
                                    <RowActions>
                                        {href && (
                                            <Tooltip title={notificationLinkLabel(href)}>
                                                <IconButton
                                                    size="small"
                                                    component={Link}
                                                    to={href}
                                                    aria-label={notificationLinkLabel(href)}
                                                    onClick={() => !notification.read && handleMarkRead(notification._id)}
                                                >
                                                    <ArrowForwardIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {!notification.read && (
                                            <Tooltip title="Mark as read">
                                                <IconButton size="small" onClick={() => handleMarkRead(notification._id)}>
                                                    <DoneIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <Tooltip title="Delete">
                                            <IconButton size="small" onClick={() => handleDelete(notification._id)}>
                                                <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </RowActions>
                                </Row>
                            );
                        })}
                    </Rows>
                    {pagination.page < pagination.pages && (
                        <LoadMoreRow>
                            <Button onClick={handleLoadMore} disabled={loadingMore}>
                                {loadingMore ? <CircularProgress size={18} /> : 'Load more'}
                            </Button>
                        </LoadMoreRow>
                    )}
                </>
            )}
        </StyledContainer>
    );
};

export default MyNotifications;
